/**
 * @file
 * HistoricalTable
 *
 * Renders the a historical table for each angler based on id, and optional
 * tournament type.
 */

import * as React from 'react';

import meta from './meta';
import { buildHistoricalTableRow } from './processing';

// Components
import ApiTable from '../../components/ApiTable/ApiTable';
import BassComponent from '../../components/BassComponent/BassComponent';

// Types
import { AnglerId } from '../../types';
import { HistoricalTableTypes } from './types';

type HistoricalTableTypeTypes = HistoricalTableTypes | 'all' | undefined;

interface IProps {
  // Fisherman Id e.g. 28709, 55161
  fishermanId: AnglerId;
  // Tournament Type e.g. classic, classicwins, top10, etc
  type?: HistoricalTableTypeTypes;
  // Select change
  selectChange?: (event: React.FormEvent<HTMLSelectElement>) => void;
}

// Visible select options
const options = [
  { val: 'all', text: 'All Entries' },
  { val: HistoricalTableTypes.CLASSIC, text: 'Classic Appearances' },
  { val: HistoricalTableTypes.CLASSIC_WINS, text: 'Classic Wins' },
  { val: HistoricalTableTypes.MONEY, text: 'Times In Money' },
  { val: HistoricalTableTypes.FIRST, text: '1st Place Finishes' },
  { val: HistoricalTableTypes.SECOND, text: '2nd Place Finishes' },
  { val: HistoricalTableTypes.THIRD, text: '3rd Place Finishes' },
  { val: HistoricalTableTypes.TOP_10, text: 'Top 10 Finishes' },
  { val: HistoricalTableTypes.TOP_20, text: 'Top 20 Finishes' },
  { val: HistoricalTableTypes.TOP_30, text: 'Top 30 Finishes' },
];

export const HistoricalTable = ({
  fishermanId,
  type,
  selectChange,
}: IProps) => {
  const apiUri = `/wp-json/data/v1/angler/tournaments?angler_id=${fishermanId}&tournament_type=${type}`;

  return (
    <div data-testid="historical-table-results">
      <BassComponent
        meta={{
          ...meta,
          ...{ nickname: `HistoricalTable${type ? `: ${type}` : ''}` },
        }}
      >
        <select
          name="results_page"
          id="select"
          className="form-select"
          onChange={selectChange}
          value={type}
        >
          {options.map(({ val, text }) => (
            <option key={val} value={val}>
              {text}
            </option>
          ))}
        </select>
        <ApiTable
          apiUri={apiUri}
          tableRowFormatter={buildHistoricalTableRow}
          rootCssClass="historical-table"
        />
      </BassComponent>
    </div>
  );
};

/**
 * Change url based on select dropdown change
 * @param event
 */
const dropdownChange = (event: React.FormEvent<HTMLSelectElement>) => {
  const selectFilter = event.currentTarget.value;
  window.location.href = `${window.location.pathname}?filter=${selectFilter}`;
};

/**
 * Check for filter in URL query to override type prop
 * @param props
 */
export const HOCHistoricalTable: React.FunctionComponent<IProps> = props => {
  const filter = new URLSearchParams(window.location.search).get(
    'filter'
  ) as HistoricalTableTypeTypes;

  const newProps = {
    ...props,
    ...(filter && { type: filter }),
    selectChange: dropdownChange,
  };

  return <HistoricalTable {...newProps} />;
};

export default HOCHistoricalTable;
